import { Box, Avatar, FileButton, Button, Group, Stack, Text } from '@mantine/core';
import { IconCamera, IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { api } from '../api/client.ts';
import { notifications } from '@mantine/notifications';

/**
 * Visitor image widget. Three modes:
 *   - readOnly:  just shows `/api/visitors-image/{visitorId}` as an img src.
 *   - editable + new visitor (id == null): uploads to a sentinel id, returns
 *     a tempId in `pendingTempId` for the parent form to promote after save.
 *   - editable + existing visitor: uploads to /api/visitors-image-temp/{id},
 *     stashes tempId, and the parent calls `promote()` after PUT succeeds.
 *
 * The legacy frontend uses 9007199254740991 (Number.MAX_SAFE_INTEGER) as the
 * sentinel id for "not saved yet" — we follow the same convention so the
 * backend's path layout doesn't change.
 */
export const NEW_VISITOR_ID_SENTINEL = 9007199254740991;

export function VisitorImage({
  visitorId,
  editable = false,
  pendingTempId,
  onTempUploaded,
}: {
  visitorId: number | null | undefined;
  editable?: boolean;
  pendingTempId?: string | null;
  onTempUploaded?: (tempId: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  // Cache-buster bumped after an upload so the permanent-slot <img> refetches
  // once the temp image is promoted (same URL would otherwise show the cached
  // old photo).
  const [bust, setBust] = useState(0);
  const src = pendingTempId
    ? `/api/visitors-image/${pendingTempId}`
    : `/api/visitors-image/${visitorId ?? -1}${bust ? `?v=${bust}` : ''}`;

  async function upload(file: File | null) {
    if (!file) return;
    const targetId = visitorId ?? NEW_VISITOR_ID_SENTINEL;
    const fd = new FormData();
    fd.append('file', file, file.name);
    setUploading(true);
    try {
      const res = await api.post(`/api/visitors-image-temp/${targetId}`, fd, {
        headers: { 'Content-Type': undefined as unknown as string },
      });
      if (res.status !== 200) throw new Error(`upload failed: ${res.status}`);
      const body = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
      if (body.tempId) {
        onTempUploaded?.(body.tempId);
        setBust(Date.now());
        notifications.show({ message: 'Image uploaded. Save the visitor to apply.', color: 'green' });
      }
    } catch (err) {
      notifications.show({
        message: 'Image upload failed' + (err instanceof Error ? `: ${err.message}` : ''),
        color: 'red',
      });
    } finally {
      setUploading(false);
    }
  }

  return (
    <Stack gap={4} align="center">
      <Avatar src={src} size={96} radius="md">
        <IconCamera size={28} />
      </Avatar>
      {editable && (
        <FileButton onChange={upload} accept="image/jpeg,image/png">
          {(props) => (
            <Button {...props} variant="subtle" size="xs" leftSection={<IconUpload size={12} />} loading={uploading}>
              Change
            </Button>
          )}
        </FileButton>
      )}
      {pendingTempId && (
        <Text size="xs" c="dimmed">Pending — saves on visitor save</Text>
      )}
    </Stack>
  );
}

/**
 * After a visitor is saved, call this to promote a previously-uploaded temp
 * image to the visitor's permanent slot. Safe to call with null/undefined.
 */
export async function promoteVisitorImage(visitorId: number, tempId: string | null | undefined): Promise<boolean> {
  if (!tempId) return true; // nothing to promote
  try {
    const res = await api.get(`/api/visitors-image/${visitorId}/${tempId}`);
    return res.status >= 200 && res.status < 300;
  } catch {
    return false;
  }
}
